import { Loader } from '@jutro/components';
import { IntlContext } from '@jutro/locale';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import styles from './ClauseComponent.module.scss';

import { CheckboxField, CurrencyField } from '@jutro/legacy/components';

/**
 * @memberof ent-components-platform-react.ClauseComponent
 * @prop {Object} propTypes - the props that are passed to this component
 * @prop {string} propTypes.displayName - name of clause to display
 * @prop {bool} propTypes.readOnly - if the clause selection is read only
 * @prop {bool} propTypes.value - is the clause is seleceted
 * @prop {function} propTypes.onValueChange - callback when change is made
 * @prop {string} propTypes.path - path to value in the view modal
 * @prop {bool} propTypes.isLoading - should the clause be loading
 * @prop {string} propTypes.loadingMessage - message to be shown while loading
 * @prop {string} propTypes.containerClassName - clause container class
 * @prop {bool} propTypes.showAmount - determine to show amount next to displayName
 *
 * @returns {Node} the Clause component DOM Node
 */
function ClauseComponent({
    isEditable,
    displayName,
    amount,
    readOnly,
    value,
    id,
    isLoading,
    loadingMessage,
    children,
    path,
    checked,
    description,
    containerClassName,
    showAmount,
    onValueChange,
    showError,
    showCoverageNameAmount
}) {
    const intlContext = useContext(IntlContext);
    const handleChange = useCallback(
        changedValue => {
            if (onValueChange) {
                onValueChange(changedValue, path);
            }
        },
        [onValueChange, path]
    );

    const labelNameWithAmount = useMemo(() => {
        if (!_.isUndefined(amount) && showCoverageNameAmount) {
            const formatterCurrency = intlContext.formatNumber(amount.amount, {
                style: 'currency',
                currency: amount.currency,
                // currencyDisplay: 'code',
            });

            // US26345 - customized label as per TWIA requirement
            if(!showError){
                if(amount.included_Ext){
                    return (<p>{`${displayName}`}&nbsp;&nbsp;<b>(INCLUDED)</b></p>);
                }
    
                return (<p>{`${displayName}`}&nbsp;&nbsp;<b>{`(Premium: ${formatterCurrency})`}</b></p>);
            }
            // return `${displayName} ${formatterCurrency}`;
        }

        return displayName;
    }, [amount, showCoverageNameAmount, displayName, intlContext, showError]);

    const premiumAmount_Ext = useMemo(() => {
        if (!_.isUndefined(amount) && showAmount) {
            const formatterCurrency = intlContext.formatNumber(amount.amount, {
                style: 'currency',
                currency: amount.currency,
                // currencyDisplay: 'code',
            });

            // US26330,  DE15814 - For Coverage Summary screen
            if(amount.included_Ext){
                return (<p><b>INCLUDED</b></p>);
            }

            return (<p><b>{formatterCurrency}</b></p>);
            // return `${displayName} ${formatterCurrency}`;
        }

        return null;
    }, [amount, showAmount, intlContext]);

    const hasChildren = !_.isNil(children);
    const clauseContainerStyles = classNames(styles.clause, containerClassName);
    const checkboxControlStyles = classNames({
        [styles.clauseAndTermSpacing]: hasChildren,
    });
    const checkboxStyles = classNames({
        [styles.clauseNoPadding]: !hasChildren,
    });

    return isEditable ? (
        <div className={clauseContainerStyles}>
            {isLoading ? (
                <Loader loaded={!isLoading} text={loadingMessage} />
            ) : (
                <CheckboxField
                    id={id}
                    label={labelNameWithAmount}
                    value={value}
                    readOnly={readOnly}
                    onValueChange={handleChange}
                    className={checkboxStyles}
                    controlClassName={checkboxControlStyles}
                    path={path}
                    checked={checked}
                    showInlineLabel
                    tooltip={
                        !_.isUndefined(description)
                            ? { text: description }
                            : null
                    }
                    layout="full-width"
                >
                    {value ? children : null}
                </CheckboxField>
            )}
        </div>
    ) : (
        // DE15833 - Added Condition for the Coverages that are not selected in the Quote screen
        //          should not be displayed in the Coverage Summary screen.(when checked id true)
        <div className={styles.readOnlyGrid}>
            <span className={styles.readOnlyGridSlotLeft}>
                {checked  ? displayName : null}
                <span className={styles.readOnlyGridAmount}>
                    {!_.isUndefined(amount) && showCoverageNameAmount ? (
                        <CurrencyField
                            id={id}
                            value={amount}
                            showFractions
                            readOnly
                        />
                    ) : undefined}
                </span>
            </span>
            <div className={styles.readOnlyGridSlotCenter}>
                {children}
            </div>
            <div className={styles.readOnlyGridSlotRight}>
                {premiumAmount_Ext}
            </div>
        </div>
    );
}

ClauseComponent.propTypes = {
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    onValueChange: PropTypes.func.isRequired,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    value: PropTypes.bool,
    path: PropTypes.string,
    isLoading: PropTypes.bool,
    loadingMessage: PropTypes.string,
    checked: PropTypes.bool,
    isEditable: PropTypes.bool,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    containerClassName: PropTypes.string,
    amount: PropTypes.shape({}),
    showAmount: PropTypes.bool,
};

ClauseComponent.defaultProps = {
    readOnly: false,
    path: undefined,
    value: undefined,
    isLoading: false,
    loadingMessage: '',
    checked: false,
    children: undefined,
    isEditable: true,
    description: undefined,
    containerClassName: undefined,
    amount: undefined,
    showAmount: true,
};

export default ClauseComponent;
